/**
 * Pattern-based detectors for the direct identifiers that have a recognisable shape:
 * email addresses, phone numbers, web addresses, dates, handles, IPv4 addresses,
 * postcodes and ID numbers.
 *
 * Detectors are deliberately conservative where a false positive would damage the
 * data (a year, a version number, a score) and deliberately blunt where a miss would
 * leak a person (an email address is always replaced). None of them can recognise a
 * name, a place or an employer: those come from the reader's own lists.
 */

export type PatternCategory =
  | "email"
  | "phone"
  | "url"
  | "handle"
  | "ip"
  | "postcode"
  | "id"
  | "date";

export const PATTERN_CATEGORIES: readonly PatternCategory[] = [
  "email",
  "phone",
  "url",
  "handle",
  "ip",
  "postcode",
  "id",
  "date",
];

export interface PatternHit {
  start: number;
  end: number;
  category: PatternCategory;
  /** What makes two hits "the same thing": the same address, the same number. */
  identity: string;
}

const MONTH =
  "(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)";
const DAY = "\\d{1,2}(?:st|nd|rd|th)?";

const RE_EMAIL = /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+/gu;
const RE_URL = /\b(?:https?:\/\/|www\.)[^\s<>"“”]+/gi;
const RE_URL_TAIL = /[.,;:!?)\]}'’]+$/;
const RE_HANDLE = /(?<![\p{L}\p{N}_@./])@[A-Za-z0-9_]{2,30}\b/gu;
const RE_IP = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;

const RE_PHONE_INTL =
  /(?<![\d+])\+\d{1,3}[\s.-]?\(?\d{1,4}\)?(?:[\s.-]?\d{2,4}){2,4}\b/g;
const RE_PHONE_US =
  /(?<!\d)(?:\+?1[\s.-])?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}(?!\d)/g;
const RE_PHONE_NATIONAL =
  /(?<![\d+])(?:\(0\d{2,4}\)|0\d{1,4})[\s.-]?\d{3,4}[\s.-]?\d{3,4}(?![\d-])/g;
const RE_PHONE_LOCAL = /(?<![\d-])\d{3}[-.]\d{4}(?![\d-])/g;
const RE_NON_DIGIT = /\D/g;
const RE_SPACE_RUN = /\s+/g;
const RE_TRAILING_SLASH = /\/$/;

const RE_DATE_DMY = new RegExp(
  `\\b${DAY}\\s+(?:of\\s+)?${MONTH}(?:,?\\s+\\d{4})?(?![\\p{L}\\p{N}])`,
  "gu"
);
const RE_DATE_MDY = new RegExp(
  `\\b${MONTH}\\s+${DAY}(?:,?\\s+\\d{4})?(?![\\p{L}\\p{N}])`,
  "gu"
);
const RE_DATE_MONTH_YEAR = new RegExp(`\\b${MONTH}\\s+\\d{4}\\b`, "g");
const RE_DATE_ISO = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
const RE_DATE_NUMERIC = /\b(\d{1,2})([/.-])(\d{1,2})\2(\d{4}|\d{2})\b/g;
const RE_YEAR = /\b(?:1[89]\d{2}|20\d{2})\b/;

const RE_POSTCODE_UK = /\b[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}\b/g;
const RE_POSTCODE_CA =
  /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d\b/g;
const RE_POSTCODE_US = /\b([A-Z]{2}) \d{5}(?:-\d{4})?\b/g;
const RE_ZIP4 = /\b\d{5}-\d{4}\b/g;
const US_STATES = new Set(
  "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(
    " "
  )
);

const RE_SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const RE_NINO =
  /\b[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z] ?\d{2} ?\d{2} ?\d{2} ?[A-D]\b/g;
const RE_CARD = /\b(?:\d[ -]?){12,18}\d\b/g;

const MAX_PHONE_DIGITS = 15;
const MAX_MONTH = 12;
const MAX_DAY = 31;
const MAX_OCTET = 255;

function digitsOf(value: string): string {
  return value.replace(RE_NON_DIGIT, "");
}

/** Luhn checksum: separates card numbers from other long digit runs. */
export function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = Number(digits[i]);
    if (double) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    double = !double;
  }
  return digits.length > 0 && sum % 10 === 0;
}

function collapse(value: string): string {
  return value.replace(RE_SPACE_RUN, " ").trim().toLowerCase();
}

function scan(
  text: string,
  regex: RegExp,
  category: PatternCategory,
  identity: (value: string) => string,
  accept: (match: RegExpMatchArray) => boolean = () => true
): PatternHit[] {
  const hits: PatternHit[] = [];
  for (const match of text.matchAll(regex)) {
    if (match.index === undefined || !accept(match)) {
      continue;
    }
    hits.push({
      start: match.index,
      end: match.index + match[0].length,
      category,
      identity: identity(match[0]),
    });
  }
  return hits;
}

function urlHits(text: string): PatternHit[] {
  const hits: PatternHit[] = [];
  for (const match of text.matchAll(RE_URL)) {
    if (match.index === undefined) {
      continue;
    }
    const trimmed = match[0].replace(RE_URL_TAIL, "");
    if (trimmed.length < 5) {
      continue;
    }
    hits.push({
      start: match.index,
      end: match.index + trimmed.length,
      category: "url",
      identity: trimmed.toLowerCase().replace(RE_TRAILING_SLASH, ""),
    });
  }
  return hits;
}

function phoneHits(text: string): PatternHit[] {
  const identity = (value: string) => digitsOf(value);
  const inRange = (min: number) => (match: RegExpMatchArray) => {
    const length = digitsOf(match[0]).length;
    return length >= min && length <= MAX_PHONE_DIGITS;
  };
  return [
    ...scan(text, RE_PHONE_INTL, "phone", identity, inRange(8)),
    ...scan(text, RE_PHONE_US, "phone", identity, inRange(10)),
    ...scan(text, RE_PHONE_NATIONAL, "phone", identity, inRange(9)),
    ...scan(text, RE_PHONE_LOCAL, "phone", identity),
  ];
}

function validDayMonth(first: number, second: number): boolean {
  if (first < 1 || second < 1 || first > MAX_DAY || second > MAX_DAY) {
    return false;
  }
  return first <= MAX_MONTH || second <= MAX_MONTH;
}

function numericDateOk(match: RegExpMatchArray): boolean {
  const first = Number(match[1]);
  const second = Number(match[3]);
  const separator = match[2];
  const year = match[4] ?? "";
  if (separator === "." && year.length !== 4) {
    return false;
  }
  return validDayMonth(first, second);
}

function isoDateOk(match: RegExpMatchArray): boolean {
  const month = Number(match[2]);
  const day = Number(match[3]);
  return month >= 1 && month <= MAX_MONTH && day >= 1 && day <= MAX_DAY;
}

function dateHits(text: string): PatternHit[] {
  const identity = collapse;
  return [
    ...scan(text, RE_DATE_DMY, "date", identity),
    ...scan(text, RE_DATE_MDY, "date", identity),
    ...scan(text, RE_DATE_MONTH_YEAR, "date", identity),
    ...scan(text, RE_DATE_ISO, "date", identity, isoDateOk),
    ...scan(text, RE_DATE_NUMERIC, "date", identity, numericDateOk),
  ];
}

/** The four-digit year inside a date, or null when the date states none. */
export function yearOfDate(value: string): string | null {
  return value.match(RE_YEAR)?.[0] ?? null;
}

function ipHits(text: string): PatternHit[] {
  return scan(
    text,
    RE_IP,
    "ip",
    (value) => value,
    (match) => match[0].split(".").every((part) => Number(part) <= MAX_OCTET)
  );
}

function postcodeHits(text: string): PatternHit[] {
  const identity = (value: string) => value.replace(/[\s-]/g, "").toUpperCase();
  return [
    ...scan(text, RE_POSTCODE_UK, "postcode", identity),
    ...scan(text, RE_POSTCODE_CA, "postcode", identity),
    ...scan(text, RE_POSTCODE_US, "postcode", identity, (match) =>
      US_STATES.has(match[1] ?? "")
    ),
    ...scan(text, RE_ZIP4, "postcode", identity),
  ];
}

function idHits(text: string): PatternHit[] {
  const identity = (value: string) => value.replace(/[\s-]/g, "").toUpperCase();
  return [
    ...scan(text, RE_SSN, "id", identity),
    ...scan(text, RE_NINO, "id", identity),
    ...scan(text, RE_CARD, "id", identity, (match) => {
      const digits = digitsOf(match[0]);
      return digits.length >= 13 && digits.length <= 19 && luhnValid(digits);
    }),
  ];
}

const DETECTORS: Record<PatternCategory, (text: string) => PatternHit[]> = {
  email: (text) => scan(text, RE_EMAIL, "email", (v) => v.toLowerCase()),
  phone: phoneHits,
  url: urlHits,
  handle: (text) => scan(text, RE_HANDLE, "handle", (v) => v.toLowerCase()),
  ip: ipHits,
  postcode: postcodeHits,
  id: idHits,
  date: dateHits,
};

/** Every hit for the categories switched on. Hits may overlap; the caller resolves that. */
export function detectPatterns(
  text: string,
  enabled: Readonly<Record<PatternCategory, boolean>>
): PatternHit[] {
  const hits: PatternHit[] = [];
  for (const category of PATTERN_CATEGORIES) {
    if (enabled[category]) {
      hits.push(...DETECTORS[category](text));
    }
  }
  return hits;
}
